import {
  Component,
  Input,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { Router } from '@angular/router';

import {
  ClassToggleService,
  HeaderComponent,
  ButtonModule,
  DropdownModule,
  SidebarModule,
  AvatarModule,
  HeaderModule,
  BreadcrumbModule,
  ContainerComponent,
  NavModule,
  UtilitiesModule,
} from '@coreui/angular';
import { JWTTokenService, User } from 'src/app/services/jwt-token.service';
import { IconDirective } from '@coreui/icons-angular';
import { TranslateModule } from '@ngx-translate/core';
import { CurrentUserService } from 'src/app/services/current-user.service';
import { AuthStateService } from 'src/app/services/auth-state.service';

@Component({
  selector: 'app-default-header',
  standalone: true,
  imports: [
    DropdownModule,
    ContainerComponent,
    AvatarModule,
    HeaderModule,
    BreadcrumbModule,
    SidebarModule,
    ButtonModule,
    IconDirective,
    NavModule,
    UtilitiesModule,
    TranslateModule,
  ],
  templateUrl: './default-header.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DefaultHeaderComponent extends HeaderComponent implements OnInit {
  @Input() sidebarId: string = 'sidebar';

  public newMessages = new Array(4);
  public newTasks = new Array(5);
  public newNotifications = new Array(5);

  public user: User | null = null;
  public fullname: string = '';
  public firstname: string = '';
  public lastname: string = '';
  public avatar: string | null = null;

  constructor(
    private readonly classToggler: ClassToggleService,
    private readonly tokenService: JWTTokenService,
    private readonly router: Router,
    private readonly currentUserService: CurrentUserService,
    private readonly cdr: ChangeDetectorRef,
    private readonly authState: AuthStateService,
  ) {
    super();
  }

  ngOnInit(): void {
    // Load user from /api/me instead of localStorage
    const isAuthenticated = this.authState.isAuthenticated();

    if (isAuthenticated) {
      this.currentUserService.getCurrentUser().subscribe({
        next: (user) => {
          this.firstname = user.firstname;
          this.lastname = user.lastname;
          this.fullname = user.fullname;
          this.avatar = user.avatar;
          // Force change detection with OnPush strategy
          this.cdr.markForCheck();
        },
        error: () => {
          // Fallback to AuthStateService if API fails
          this.loadFromAuthState();
          this.cdr.markForCheck();
        },
      });
    } else {
      this.loadFromAuthState();
    }
  }

  private loadFromAuthState(): void {
    const userInfo = this.authState.getUserInfo();
    if (userInfo) {
      this.fullname = userInfo.fullname || 'User';
      this.firstname = userInfo.firstname || '';
      this.lastname = userInfo.lastname || '';
      this.avatar = userInfo.avatar || null;
    } else {
      this.fullname = 'User';
    }
  }

  get userInitials(): string {
    if (this.firstname && this.lastname) {
      return (this.firstname[0] + this.lastname[0]).toUpperCase();
    }
    if (!this.fullname || this.fullname === 'User') {
      return 'U';
    }
    const names = this.fullname.split(' ');
    if (names.length >= 2) {
      return (names[0][0] + names[1][0]).toUpperCase();
    }
    return this.fullname.slice(0, 2).toUpperCase();
  }

  goToSecuritySettings(event?: Event): void {
    event?.preventDefault();
    this.router.navigateByUrl('/security-settings');
  }

  logout(event?: Event): void {
    event?.preventDefault();
    this.tokenService.clear();
    this.currentUserService.clearUser();
    this.authState.clearAuthState();
    this.router.navigateByUrl('/login');
  }
}
